
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactosPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
    if (!formData.email.trim()) newErrors.email = 'Email é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email inválido';
    if (!formData.subject.trim()) newErrors.subject = 'Assunto é obrigatório';
    if (!formData.message.trim()) newErrors.message = 'Mensagem é obrigatória';
    else if (formData.message.length > 500) newErrors.message = 'Mensagem não pode exceder 500 caracteres';
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitStatus('sending');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('subject', formData.subject);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d6k28nn51pef945vq3gg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 flex items-end pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/db91b132a274d8d0839a486ff730bd2d.jpg"
            alt="Machico, Madeira"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60"></div>
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-white/70 text-sm uppercase tracking-widest mb-2 font-medium">Estamos aqui para ajudar</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Fale Connosco
          </h1>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="bg-slate-900 py-5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            <a
              href="https://wa.me/351962050526"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-white hover:text-green-400 transition-colors cursor-pointer group"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-500 group-hover:bg-green-400 transition-colors">
                <i className="ri-whatsapp-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wide">WhatsApp</p>
                <p className="text-sm font-semibold">+351 962 050 526</p>
              </div>
            </a>
            <div className="hidden md:block w-px h-10 bg-slate-700"></div>
            <a
              href="mailto:info@seaviewlevada.pt"
              className="flex items-center gap-3 text-white hover:text-sky-400 transition-colors cursor-pointer group"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-600 group-hover:bg-sky-500 transition-colors">
                <i className="ri-mail-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wide">Email</p>
                <p className="text-sm font-semibold">info@seaviewlevada.pt</p>
              </div>
            </a>
            <div className="hidden md:block w-px h-10 bg-slate-700"></div>
            <div className="flex items-center gap-3 text-white">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-600">
                <i className="ri-time-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wide">Disponibilidade</p>
                <p className="text-sm font-semibold">Todos os dias · 08h–22h</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Left: Info Cards */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Informações de Contacto
                </h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Estamos sempre disponíveis para responder às suas dúvidas sobre reservas, experiências ou qualquer outro assunto.
                </p>
              </div>

              {/* WhatsApp Card */}
              <a
                href="https://wa.me/351962050526?text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20os%20apartamentos."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-5 rounded-xl border border-green-100 bg-green-50 hover:bg-green-100 hover:border-green-200 transition-all cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-500 shrink-0">
                  <i className="ri-whatsapp-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm mb-0.5">WhatsApp Direto</p>
                  <p className="text-green-700 font-bold text-base">+351 962 050 526</p>
                  <p className="text-slate-500 text-xs mt-1">Resposta rápida · Clique para abrir conversa</p>
                </div>
                <div className="ml-auto w-6 h-6 flex items-center justify-center text-green-400 group-hover:translate-x-1 transition-transform">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              {/* Email Card */}
              <a
                href="mailto:info@seaviewlevada.pt"
                className="flex items-start gap-4 p-5 rounded-xl border border-sky-100 bg-sky-50 hover:bg-sky-100 hover:border-sky-200 transition-all cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-sky-600 shrink-0">
                  <i className="ri-mail-send-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm mb-0.5">Email</p>
                  <p className="text-sky-700 font-bold text-base">info@seaviewlevada.pt</p>
                  <p className="text-slate-500 text-xs mt-1">Resposta em até 24 horas</p>
                </div>
                <div className="ml-auto w-6 h-6 flex items-center justify-center text-sky-400 group-hover:translate-x-1 transition-transform">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              {/* Location Card */}
              <div className="flex items-start gap-4 p-5 rounded-xl border border-amber-100 bg-amber-50">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-500 shrink-0">
                  <i className="ri-map-pin-2-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm mb-0.5">Localização</p>
                  <p className="text-amber-700 font-bold text-base">Machico, Madeira</p>
                  <p className="text-slate-500 text-xs mt-1">Junto à Levada do Caniçal · Vista mar e montanha</p>
                </div>
              </div>

              {/* Social */}
              <div className="p-5 rounded-xl border border-slate-100 bg-slate-50">
                <p className="font-semibold text-slate-700 text-sm mb-3">Siga-nos nas redes sociais</p>
                <div className="flex items-center gap-3">
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-pink-500 to-orange-400 text-white hover:scale-110 transition-transform cursor-pointer"
                    aria-label="Instagram"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                  </a>
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white hover:scale-110 transition-transform cursor-pointer"
                    aria-label="Facebook"
                  >
                    <i className="ri-facebook-fill text-lg"></i>
                  </a>
                  <a
                    href="https://wa.me/351962050526"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-green-500 text-white hover:scale-110 transition-transform cursor-pointer"
                    aria-label="WhatsApp"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Envie-nos uma Mensagem
                </h2>
                <p className="text-slate-500 text-sm mb-8">Preencha o formulário e entraremos em contacto brevemente.</p>

                {submitStatus === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mb-5">
                      <i className="ri-check-line text-green-500 text-4xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Mensagem Enviada!</h3>
                    <p className="text-slate-500 text-sm max-w-xs">
                      Obrigado pelo contacto. Responderemos em breve ao seu email.
                    </p>
                    <button
                      onClick={() => setSubmitStatus('idle')}
                      className="mt-6 px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Enviar outra mensagem
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="contacto-form"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                          Nome Completo <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="O seu nome"
                          className={`w-full px-4 py-3 rounded-lg border text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all ${errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                          Email <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="email@exemplo.com"
                          className={`w-full px-4 py-3 rounded-lg border text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all ${errors.email ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                          Telefone / WhatsApp
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+351 9XX XXX XXX"
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                          Assunto <span className="text-red-400">*</span>
                        </label>
                        <select
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all cursor-pointer ${errors.subject ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                        >
                          <option value="">Selecione um assunto</option>
                          <option value="Reserva">Reserva de Apartamento</option>
                          <option value="Informações">Pedido de Informações</option>
                          <option value="Experiências">Experiências na Madeira</option>
                          <option value="Cancelamento">Cancelamento / Alteração</option>
                          <option value="Outro">Outro Assunto</option>
                        </select>
                        {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                        Mensagem <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        maxLength={500}
                        placeholder="Escreva a sua mensagem aqui..."
                        className={`w-full px-4 py-3 rounded-lg border text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all resize-none ${errors.message ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                      />
                      <div className="flex items-center justify-between mt-1">
                        {errors.message ? (
                          <p className="text-red-500 text-xs">{errors.message}</p>
                        ) : <span />}
                        <p className={`text-xs ml-auto ${charCount > 450 ? 'text-red-400' : 'text-slate-400'}`}>
                          {charCount}/500
                        </p>
                      </div>
                    </div>

                    {submitStatus === 'error' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                        <i className="ri-error-warning-line text-red-500"></i>
                        <p className="text-red-600 text-sm">Ocorreu um erro. Por favor tente novamente.</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitStatus === 'sending'}
                      className="w-full py-3.5 rounded-lg bg-slate-900 text-white font-semibold text-sm hover:bg-slate-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {submitStatus === 'sending' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          A enviar...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          Enviar Mensagem
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
            <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100">
                  <i className="ri-map-pin-2-fill text-amber-600 text-base"></i>
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Seaview &amp; Levada Escape</p>
                  <p className="text-slate-500 text-xs">Machico, Madeira, Portugal · Junto à Levada do Caniçal</p>
                </div>
              </div>
              <a
                href="https://maps.app.goo.gl/XWJGnzA1wZStY3Fa8"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Abrir no Google Maps
              </a>
            </div>
            <div className="w-full h-96">
              <iframe
                title="Localização Seaview & Levada Escape - Machico, Madeira"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d-16.781175!3d32.742384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDQ0JzMyLjYiTiAxNsKwNDYnNTIuMiJX!5e0!3m2!1spt!2spt!4v1700000000000!5m2!1spt!2spt"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Strip */}
      <section className="bg-slate-50 py-16 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Playfair Display', serif" }}>
              Perguntas Frequentes
            </h2>
            <p className="text-slate-500 text-sm mt-2">As dúvidas mais comuns dos nossos hóspedes</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { q: 'Qual é o horário de check-in e check-out?', a: 'Check-in a partir das 15h00 e check-out até às 11h00. Horários flexíveis mediante disponibilidade.' },
              { q: 'Os apartamentos têm estacionamento?', a: 'Sim, disponibilizamos estacionamento gratuito para todos os hóspedes junto ao alojamento.' },
              { q: 'É possível fazer reservas de última hora?', a: 'Sim! Entre em contacto via WhatsApp para verificar disponibilidade imediata.' },
              { q: 'Os animais de estimação são permitidos?', a: 'Aceitamos animais de pequeno porte mediante pedido prévio e sujeito a disponibilidade.' },
              { q: 'Como chegar ao alojamento?', a: 'Estamos a 5 minutos do centro de Machico. Enviamos instruções detalhadas após a reserva.' },
              { q: 'Qual é a política de cancelamento?', a: 'Cancelamento gratuito até 7 dias antes da chegada. Consulte os termos completos na reserva.' },
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-100 shrink-0 mt-0.5">
                    <i className="ri-question-line text-amber-600 text-xs"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm mb-1.5">{faq.q}</p>
                    <p className="text-slate-500 text-xs leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactosPage;
